<template>
  <div class="flex w-full flex-col gap-[15px]">
    <div
      v-for="product in memoizedProductList"
      :key="`bump-product-${product.priceId}-${product.productId}`"
      class="flex flex-col items-start self-stretch rounded-lg border-dashed border-gray-200"
    >
      <div
        class="flex items-center justify-between gap-2 self-stretch rounded-t-lg bg-indigo-600 px-3 py-[10px]"
        :style="{
          backgroundColor: '#' + store.payment.bumpSettings.bumpBackgroundColor,
        }"
      >
        <div class="flex items-center gap-2 self-stretch">
          <input :id="product.productId" :name="product._id" :value="product._id" type="checkbox" />
          <UITextSmMedium
            :style="{
              color: '#' + store.payment.bumpSettings.highlightTextColor,
            }"
            >{{ store.payment.bumpSettings.highlightText }}</UITextSmMedium
          >
          <img
            v-if="store.payment.bumpSettings.bumpIcon !== 'none'"
            :src="
              BUMP_ICONS.find((icon) => icon.value === store.payment.bumpSettings.bumpIcon)
                ?.imageUrl
            "
            :alt="t('common.products.bumpIconAlt')"
            class="h-6 w-6"
            loading="lazy"
          />
        </div>
      </div>
      <div class="flex items-start gap-2 self-stretch p-4">
        <div class="flex w-full items-start gap-2 self-stretch">
          <img
            v-if="
              product.enableImage &&
              (product.uploadedImage?.length > 0 || product.image?.length > 0)
            "
            :key="product.imageKey"
            :src="product.uploadedImage || product.image"
            :alt="t('common.products.productImageAlt')"
            class="h-20 w-20 rounded-lg"
            loading="lazy"
            @error="handleImageError"
          />
          <div v-else-if="product.enableImage" class="h-20 w-20 rounded-lg bg-[lightgray]"></div>

          <div class="flex w-full flex-col items-start gap-2 self-stretch">
            <div class="flex w-full items-start justify-between gap-2 self-stretch">
              <UITextSmMedium
                :style="{
                  color: '#' + store.payment.bumpSettings.headlineColor,
                }"
                >{{ product.headline }}</UITextSmMedium
              >
              <div
                v-if="store.payment.bumpSettings.showPrice"
                class="flex items-start gap-[5px] self-stretch"
                :style="{
                  color: '#' + store.payment.bumpSettings.bumpPriceColor,
                }"
              >
                <UITextSmMedium
                  v-if="product.compareAtPrice"
                  class="overflow-hidden text-ellipsis line-through"
                  :class="{
                    'line-through': product.compareAtPrice,
                  }"
                  >{{ store.currencyList[product.currency]?.symbol || "$"
                  }}{{ product.compareAtPrice }}
                </UITextSmMedium>
                <UITextSmMedium
                  >{{ store.currencyList[product.currency]?.symbol || "$"
                  }}{{ product.amount }}</UITextSmMedium
                >
              </div>
            </div>

            <UITextSmMedium
              :style="{
                color: '#' + store.payment.bumpSettings.otoHeadlineColor,
              }"
              >{{ product.otoHeadline }}</UITextSmMedium
            >
            <UITextSmMedium
              class="whitespace-pre-wrap"
              :style="{
                color: '#' + store.payment.bumpSettings.otoTextColor,
              }"
              >{{ product.otoText }}</UITextSmMedium
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { UITextSmMedium } from "@gohighlevel/ghl-ui";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { useAppStore } from "@/store/app";
import { BUMP_ICONS } from "@/util/productConstants";

const store = useAppStore();
const { t } = useI18n();

// Memoize the product list to prevent unnecessary re-renders
const memoizedProductList = computed(() => {
  return store.bumProductList.map((product) => ({
    ...product,
    imageKey: `${product.productId}-${product.uploadedImage || product.image}`,
  }));
});

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  // Set a fallback image or hide the image element
  img.style.display = "none";
};
</script>

<style scoped></style>
